import React, { useState, useEffect } from 'react';
import { useDefaultItemOwner } from './useDefaultItemOwner';
import { useEscapeToClose } from '../common/useEscapeToClose';
import { toast } from 'react-toastify';
import { RISK_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS, RiskLevel, PriorityLevel, ProjectStatus } from '../../constants/project';
import { PhaseSelectCreate } from '../PhaseSelectCreate';
import { useKeycloak } from '@react-keycloak/web';
import { getFormSelectStyle, getFormSelectContainerStyle } from '../../styles/designSystem';
import { createSafeCloseHandler } from '../../utils/modalUtils';

interface AddProjectItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddItem: (itemData: any) => Promise<void>;
  projectData: ProjectInterface;
  users: any[];
  onProjectUpdate?: () => Promise<void>;
}

interface ProjectItemFormData {
  name: string;
  description: string;
  phase_id: string;
  owner_id: string;
  assigned_to: string;
  start_date: string;
  due_date: string;
  priority: PriorityLevel;
  status: ProjectStatus;
  completion_percentage: number | null;
  risks: RiskLevel;
}

export const AddProjectItemModal: React.FC<AddProjectItemModalProps> = ({
  isOpen,
  onClose,
  onAddItem,
  projectData,
  users: usersFromProject,
  onProjectUpdate
}) => {
  const { keycloak: { token, idTokenParsed } } = useKeycloak();

  const users = usersFromProject;
  // The logged-in user's Keycloak subject is their user id, the same id the project user list uses.
  const defaultOwnerId = useDefaultItemOwner(users, projectData?.owner_id, idTokenParsed?.sub);
  
  // Helper function to get date one month from today
  const getDateOneMonthFromToday = () => {
    const today = new Date();
    const oneMonthLater = new Date(today);
    oneMonthLater.setMonth(today.getMonth() + 1);
    return oneMonthLater.toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState<ProjectItemFormData>({
    name: '',
    description: '',
    phase_id: projectData.phases?.[0]?.id || '',
    owner_id: defaultOwnerId, // Default owner, changeable
    assigned_to: '',
    start_date: new Date().toISOString().split('T')[0],
    due_date: getDateOneMonthFromToday(), // Set due date to one month from today
    priority: PRIORITY_OPTIONS[0].value,
    status: STATUS_OPTIONS[0].value,
    completion_percentage: null,
    risks: RISK_OPTIONS[0].value
  });
  const [isLoading, setIsLoading] = useState(false);
  const [dateValidationError, setDateValidationError] = useState(false);

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    onClose, 
    ['owner_id', 'phase_id', 'start_date', 'due_date', 'priority', 'status', 'completion_percentage', 'risks'] // Exclude auto-filled fields
  );

  // Function to validate dates
  const validateDates = (startDate: string, dueDate: string) => {
    if (!startDate || !dueDate) return true; // Allow empty dates
    
    const start = new Date(startDate);
    const due = new Date(dueDate);
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(due.getTime())) {
      return false;
    }
    
    // Define reasonable date bounds (e.g., 10 years ago to 10 years in the future)
    const now = new Date();
    const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
    const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());
    
    // Check if dates are within reasonable bounds
    if (start < minDate || start > maxDate || due < minDate || due > maxDate) {
      return false;
    }
    
    // Check if due date is after start date
    return due >= start;
  };

  useEscapeToClose(isOpen, onClose);

  // Update owner_id when modal opens or users change
  useEffect(() => {
    if (isOpen && defaultOwnerId && formData.owner_id !== defaultOwnerId) {
      setFormData(prev => ({
        ...prev,
        owner_id: defaultOwnerId
      }));
    }
  }, [isOpen, defaultOwnerId, formData.owner_id]);

  // Validate dates when modal opens or form data changes
  useEffect(() => {
    if (isOpen) {
      const isValid = validateDates(formData.start_date, formData.due_date);
      setDateValidationError(!isValid);
    }
  }, [isOpen, formData.start_date, formData.due_date]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    let newFormData = {
      ...formData,
      [name]: name === 'completion_percentage' 
        ? (value === '' ? null : Number(value))
        : value
    };

    // Validate dates when start_date or due_date changes
    if (name === 'start_date' || name === 'due_date') {
      const isValid = validateDates(
        name === 'start_date' ? value : formData.start_date,
        name === 'due_date' ? value : formData.due_date
      );
      setDateValidationError(!isValid);
    }

    setFormData(newFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Task name is required');
      return;
    }
    if (!formData.owner_id || !formData.owner_id.trim()) {
      toast.error('Owner is required');
      return;
    }
    if (!formData.phase_id) {
      toast.error('Phase is required');
      return;
    }
    if (!formData.assigned_to) {
      toast.error('Assign to is required');
      return;
    }

    // Validate dates before submission
    if (!validateDates(formData.start_date, formData.due_date)) {
      toast.error('Please enter valid dates within the last 10 years to the next 10 years. Due date must be after start date.');
      return;
    }

    setIsLoading(true);
    try {
      const baseData = {
        name: formData.name,
        description: formData.description,
        phase_id: formData.phase_id,
        priority: formData.priority,
        status: formData.status,
        estimated_hours: null,
        completion_percentage: formData.completion_percentage,
        risks: formData.risks,
        project_id: projectData.id,
        parent_id: null,
        actual_hours: null,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : new Date().toISOString(),
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : new Date().toISOString(),
      };
      
      // Only add owner_id and assigned_to if they have valid UUID values
      const itemData: any = { ...baseData };
      if (formData.owner_id && formData.owner_id.trim()) {
        itemData.owner_id = formData.owner_id;
      }
      if (formData.assigned_to && formData.assigned_to.trim()) {
        itemData.assigned_to = formData.assigned_to;
      }
      
      console.log('AddProjectItemModal: submitting item data', itemData);
      
      await onAddItem(itemData);
      
      console.log('AddProjectItemModal: item added successfully');
      toast.success('Task added successfully');
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        phase_id: projectData.phases?.[0]?.id || '',
        owner_id: defaultOwnerId, // Keep the default owner
        assigned_to: '',
        start_date: new Date().toISOString().split('T')[0],
        due_date: getDateOneMonthFromToday(), // Set due date to one month from today
        priority: PRIORITY_OPTIONS[0].value,
        status: STATUS_OPTIONS[0].value,
        completion_percentage: null,
        risks: RISK_OPTIONS[0].value
      });
      
      // Reset validation error
      setDateValidationError(false);
      
      onClose();
    } catch (error) {
      console.error('AddProjectItemModal: failed to add item', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add task';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={handleSafeClose}
    >
      <button
        onClick={() => handleSafeClose()}
        className="fixed top-4 right-4 z-[60] text-[#aaa] hover:text-[#555] p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#eaeaea]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Add New Task</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Required Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#444] mb-1">
                Task Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                maxLength={255}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#444] mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Phase <span className="text-red-500">*</span>
              </label>
                <PhaseSelectCreate
                  onChange={({value}: any) => {
                  // Ensure only one phase can be selected at a time
                  if (value && typeof value === 'string') {
                    setFormData(prev => ({ ...prev, phase_id: value }));
                  }
                  }}
                  project_id={projectData.id || ''}
                  phases={projectData?.phases || []}
                  defaultValue={formData.phase_id}
                  onCreate={async (phaseId: string) => {
                  // Ensure only one phase can be selected when creating new one
                  if (phaseId && typeof phaseId === 'string') {
                    setFormData(prev => ({ ...prev, phase_id: phaseId }));
                    // Update project data after phase creation
                    if (onProjectUpdate) {
                      await onProjectUpdate();
                    }
                    }
                  }}
                  token={token || ''}
                />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Priority
              </label>
              <div style={getFormSelectContainerStyle()}>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  style={getFormSelectStyle()}
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Status
              </label>
              <div style={getFormSelectContainerStyle()}>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={getFormSelectStyle()}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Owner <span className="text-red-500">*</span>
              </label>
              <div style={getFormSelectContainerStyle()}>
                <select
                  name="owner_id"
                  value={formData.owner_id}
                  onChange={handleInputChange}
                  style={getFormSelectStyle()}
                  required
                >
                  <option value="">Select owner</option>
                  {(users || []).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Assigned To <span className="text-red-500">*</span>
              </label>
              <div style={getFormSelectContainerStyle()}>
                <select
                  name="assigned_to"
                  value={formData.assigned_to}
                  onChange={handleInputChange}
                  style={getFormSelectStyle()}
                  required
                >
                  <option value="">Select team member</option>
                  {(users || []).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                min={(() => {
                  const now = new Date();
                  const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
                  return minDate.toISOString().split('T')[0];
                })()}
                max={(() => {
                  const now = new Date();
                  const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());
                  return maxDate.toISOString().split('T')[0];
                })()}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                  dateValidationError 
                    ? 'border-red-500 focus:ring-red-500' 
                    : 'border-[#eaeaea] focus:ring-0'
                }`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Due Date
              </label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                min={(() => {
                  const now = new Date();
                  const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
                  return minDate.toISOString().split('T')[0];
                })()}
                max={(() => {
                  const now = new Date();
                  const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());
                  return maxDate.toISOString().split('T')[0];
                })()}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                  dateValidationError 
                    ? 'border-red-500 focus:ring-red-500' 
                    : 'border-[#eaeaea] focus:ring-0'
                }`}
              />
            </div>

            {dateValidationError && (
              <div className="md:col-span-2 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-md border border-red-200">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>Please enter valid dates within the last 10 years to the next 10 years. Due date must be after start date.</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Completion %
              </label>
              <input
                type="number"
                name="completion_percentage"
                value={formData.completion_percentage || ''}
                onChange={handleInputChange}
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Risk Level
              </label>
              <div style={getFormSelectContainerStyle()}>
                <select
                  name="risks"
                  value={formData.risks}
                  onChange={handleInputChange}
                  style={getFormSelectStyle()}
                >
                  {RISK_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#444] bg-[#f5f5f5] rounded-md hover:bg-[#eaeaea] focus:outline-none focus:ring-0"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || dateValidationError}
              className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] focus:outline-none focus:ring-0 disabled:opacity-50"
            >
              {isLoading ? 'Adding...' : 'Add Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 